import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
} from '@mui/material';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import {
  KeyboardEvent,
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import css from './DataTable.module.scss';
import {
  createRowFilter,
  getCellValue,
  getOperators,
  isFilterItemActive,
  withoutEmptySections,
} from './filtering';
import { FilterPanel } from './FilterPanel';
import { renderedCellIndex } from './navigation';
import { moveColumn, selectedIds, toggleVisibleSelection } from './selection';
import { fitColumnWidths } from './sizing';
import { TableCheckbox } from './TableCheckbox';
import { TableSearchField, useSearchText } from './TableSearchField';
import type {
  DataTableApi,
  DataTableProps,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
  TableEvent,
} from './types';

const EMPTY_FILTER: GridFilterModel = { items: [] };
const EMPTY_SORT: GridSortModel = [];
const EMPTY_SELECTION: GridRowSelectionModel = { type: 'include', ids: new Set() };
const EMPTY_VISIBILITY: GridColumnVisibilityModel = {};
const defaultRowId = (row: GridValidRowModel): GridRowId => row.id;
export const GRID_CHECKBOX_SELECTION_COL_DEF: GridColDef = {
  field: '__check__',
  headerName: 'Select',
  width: 48,
  minWidth: 48,
  maxWidth: 48,
  align: 'center',
  sortable: false,
  filterable: false,
  hideable: false,
  resizable: false,
  disableReorder: true,
  disableColumnMenu: true,
  renderCell: (params) => params.selectionControl,
};
export const useGridApiRef = () => useRef<DataTableApi | null>(null);
export const gridFilteredSortedRowIdsSelector = (
  ref: RefObject<DataTableApi | null>,
): GridRowId[] => ref.current?.getFilteredSortedRowIds() ?? [];

/** TanStack owns the row models and sizing; this component owns the app's UI and persisted contracts. */
export function DataTable<R extends GridValidRowModel>(props: DataTableProps<R>) {
  const { rows, columns, rowHeight = 52, columnHeaderHeight = 56, getRowId = defaultRowId } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const internalApiRef = useGridApiRef();
  const apiRef = props.apiRef ?? internalApiRef;
  const listeners = useRef(new Map<TableEvent, Set<() => void>>());
  const [internalSort, setInternalSort] = useState(
    props.initialState?.sorting?.sortModel ?? EMPTY_SORT,
  );
  const [internalFilter, setInternalFilter] = useState(EMPTY_FILTER);
  const [internalSelection, setInternalSelection] = useState(EMPTY_SELECTION);
  const [internalVisibility, setInternalVisibility] = useState(
    props.initialState?.columns?.columnVisibilityModel ?? EMPTY_VISIBILITY,
  );
  const [order, setOrder] = useState<string[]>([]);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
  const searchIsExternal = !!props.onSearchChange;
  const [search, changeSearch] = useSearchText(props.searchValue, props.onSearchChange);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // The table filters by name itself only when the search is its own.
  const ownQuery = searchIsExternal ? '' : search;
  const [menu, setMenu] = useState<{ anchor: HTMLElement; field: string } | null>(null);
  const [panel, setPanel] = useState<{
    position: { top: number; left: number };
    type: 'columns' | 'filters';
  } | null>(null);
  const [columnSearch, setColumnSearch] = useState('');
  const [focus, setFocus] = useState<{ row: GridRowId; field: string } | null>(null);
  const dragColumn = useRef<string | null>(null);
  // Whether the last press on a header was on its resize handle. The header is draggable (to
  // reorder), and the browser fires that drag on the header rather than on the handle, so the
  // header has to refuse it. A drag that starts mid-resize swallows the mouse release, which left
  // the column resizing after the button was up and dropped it somewhere else.
  const pressedResizeHandle = useRef(false);
  const selectionAnchor = useRef<GridRowId | null>(null);
  const pendingOrder = useRef(false);
  const sortModel = props.sortModel ?? internalSort;
  const filterModel = props.filterModel ?? internalFilter;
  const selectionModel = props.rowSelectionModel ?? internalSelection;
  const visibility = props.columnVisibilityModel ?? internalVisibility;

  const setSort = (model: GridSortModel) => {
    setInternalSort(model);
    props.onSortModelChange?.(model);
  };
  const setFilter = (model: GridFilterModel) => {
    setInternalFilter(model);
    props.onFilterModelChange?.(model);
  };
  const setSelection = (model: GridRowSelectionModel | GridRowId[]) => {
    const next = Array.isArray(model) ? { type: 'include' as const, ids: new Set(model) } : model;
    setInternalSelection(next);
    props.onRowSelectionModelChange?.(next);
  };
  const setVisibility = (model: GridColumnVisibilityModel) => {
    setInternalVisibility(model);
    props.onColumnVisibilityModelChange?.(model);
  };

  const allColumns = useMemo(
    () =>
      props.checkboxSelection && !columns.some((c) => c.field === '__check__')
        ? [GRID_CHECKBOX_SELECTION_COL_DEF as GridColDef<R>, ...columns]
        : [...columns],
    [columns, props.checkboxSelection],
  );
  // A caller's saved column order is authoritative when the definitions change.
  const definitionOrder = allColumns.map((column) => column.field).join('\0');
  const previousColumns = useRef(definitionOrder);
  const effectiveOrder = previousColumns.current === definitionOrder ? order : [];
  useLayoutEffect(() => {
    if (previousColumns.current !== definitionOrder) {
      previousColumns.current = definitionOrder;
      setOrder([]);
    }
  }, [definitionOrder]);
  const orderedColumns = useMemo(() => {
    const known = new Set(allColumns.map((c) => c.field));
    const ordered = effectiveOrder.filter((field) => known.has(field));
    allColumns.forEach((c) => {
      if (!ordered.includes(c.field)) ordered.push(c.field);
    });
    return ordered.map((field) => allColumns.find((c) => c.field === field)!);
  }, [allColumns, effectiveOrder]);
  const fixedFields = useMemo(
    () => new Set(allColumns.filter((c) => c.disableReorder).map((c) => c.field)),
    [allColumns],
  );
  const rowIds = useMemo(() => rows.map(getRowId), [rows, getRowId]);
  const selected = selectedIds(selectionModel, rowIds);
  const rowFilterState = useMemo(() => ({ filterModel, ownQuery }), [filterModel, ownQuery]);
  const predicate = useMemo(() => {
    const matchesFilters = createRowFilter(allColumns, filterModel);
    const nameColumn = allColumns.find((column) => column.field === props.searchField);
    const query = ownQuery.trim().toLocaleLowerCase();
    return (row: R) =>
      !!props.isSectionRow?.(row) ||
      (matchesFilters(row) &&
        (!query ||
          !nameColumn ||
          String(getCellValue(row, nameColumn) ?? '')
            .toLocaleLowerCase()
            .includes(query)));
  }, [allColumns, filterModel, ownQuery, props.searchField, props.isSectionRow]);
  const visibleDefinitions = orderedColumns.filter(
    (c) => visibility[c.field] !== false || c.hideable === false,
  );
  const fittedWidths = fitColumnWidths(visibleDefinitions, containerWidth, columnSizing);
  const widthsKey = JSON.stringify(fittedWidths);
  const sortRef = useRef(sortModel);
  sortRef.current = sortModel;
  const tableColumns = useMemo<ColumnDef<R>[]>(
    () =>
      orderedColumns.map((column, index) => ({
        id: column.field,
        accessorFn: (row) => getCellValue(row, column),
        size: fittedWidths[column.field] ?? column.width ?? column.minWidth ?? 100,
        minSize: column.minWidth ?? 50,
        maxSize: column.maxWidth ?? 2000,
        enableResizing: column.resizable !== false,
        enableSorting: column.sortable !== false,
        enableHiding: column.hideable !== false,
        enableGlobalFilter: index === 0,
        sortingFn: (a, b) => {
          const av = a.getValue(column.field);
          const bv = b.getValue(column.field);
          if (column.sortComparator) {
            const api = { getSortModel: () => sortRef.current };
            return column.sortComparator(av, bv, { api }, { api });
          }
          if (av == null) return bv == null ? 0 : -1;
          if (bv == null) return 1;
          if (typeof av === 'number' && typeof bv === 'number') return av - bv;
          if (av instanceof Date && bv instanceof Date) return av.getTime() - bv.getTime();
          return String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        },
      })),
    [orderedColumns, widthsKey],
  );
  const tableVisibility = { ...visibility };
  allColumns.forEach((c) => {
    if (c.hideable === false) tableVisibility[c.field] = true;
  });
  const sortKey = JSON.stringify(sortModel);
  const sorting = useMemo(
    () =>
      (JSON.parse(sortKey) as GridSortModel)
        .filter((s) => s.sort)
        .map((s) => ({ id: s.field, desc: s.sort === 'desc' })),
    [sortKey],
  );
  const table = useReactTable({
    data: rows as R[],
    columns: tableColumns,
    getRowId: (row) => String(getRowId(row)),
    state: {
      sorting,
      globalFilter: rowFilterState,
      columnVisibility: tableVisibility,
      columnSizing,
    },
    getColumnCanGlobalFilter: () => true,
    globalFilterFn: (row) => predicate(row.original),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: props.sortingMode === 'server',
    columnResizeMode: 'onChange',
    onColumnSizingChange: setColumnSizing,
  });
  const modelRows = table.getRowModel().rows;
  // Memoized: effects below key on this array's identity.
  const visibleRows = useMemo(
    () =>
      props.isSectionRow
        ? withoutEmptySections(modelRows, (row) => props.isSectionRow!(row.original))
        : modelRows,
    [modelRows, props.isSectionRow],
  );
  const visibleColumns = table.getVisibleLeafColumns();
  const spacing = (index: number) =>
    props.getRowSpacing?.({
      isFirstVisible: index === 0,
      isLastVisible: index === visibleRows.length - 1,
    }) ?? { top: 0, bottom: 0 };
  const height = (index: number) =>
    props.getRowHeight?.({
      id: getRowId(visibleRows[index].original),
      model: visibleRows[index].original,
    }) ?? rowHeight;
  const virtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => height(index) + spacing(index).top + spacing(index).bottom,
    getItemKey: (index) => visibleRows[index].id,
    overscan: 8,
  });
  // Density and synthetic section-row heights can change without row IDs changing.
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rowHeight, props.getRowHeight, visibleRows, virtualizer]);
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const measure = () => {
      setContainerWidth(element.clientWidth);
      setScrollbarWidth(element.offsetWidth - element.clientWidth);
    };
    // The content box shrinks when a vertical scrollbar appears, so this also catches that.
    const observer = new ResizeObserver(measure);
    measure();
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  // The header sits outside the rows' scroller, so the vertical scrollbar starts below it and no
  // row can paint over it. It follows the rows sideways, and a header scrolled by focus moving onto
  // an off-screen column scrolls the rows with it.
  const syncScroll = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (from && to && to.scrollLeft !== from.scrollLeft) to.scrollLeft = from.scrollLeft;
  };
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    // An `overflow: hidden` strip doesn't scroll from the wheel or a trackpad, so hand that to the
    // rows' scroller. Not a React handler, because React's wheel listeners are passive.
    const onWheel = (event: WheelEvent) => {
      const scroller = scrollRef.current;
      if (!scroller) return;
      const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientHeight : 1;
      event.preventDefault();
      scroller.scrollBy({ left: event.deltaX * scale, top: event.deltaY * scale });
    };
    header.addEventListener('wheel', onWheel, { passive: false });
    return () => header.removeEventListener('wheel', onWheel);
  }, []);

  const selectableVisibleIds = visibleRows
    .filter((r) => props.isRowSelectable?.({ id: getRowId(r.original), row: r.original }) !== false)
    .map((r) => getRowId(r.original));
  const focusIsVisible =
    focus &&
    visibleColumns.some((column) => column.id === focus.field) &&
    visibleRows.some((row) => getRowId(row.original) === focus.row);
  const selectedVisibleCount = selectableVisibleIds.filter((id) => selected.has(id)).length;
  const allVisibleSelected =
    selectableVisibleIds.length > 0 && selectedVisibleCount === selectableVisibleIds.length;
  const toggleRow = (id: GridRowId, shift = false) => {
    const next = new Set(selected);
    const select = !next.has(id);
    const end = selectableVisibleIds.indexOf(id);
    const start =
      selectionAnchor.current === null ? -1 : selectableVisibleIds.indexOf(selectionAnchor.current);
    const targets =
      shift && start >= 0 && end >= 0
        ? selectableVisibleIds.slice(Math.min(start, end), Math.max(start, end) + 1)
        : [id];
    targets.forEach((key) => (select ? next.add(key) : next.delete(key)));
    selectionAnchor.current = id;
    setSelection({ type: 'include', ids: next });
  };
  const reorder = (source: string, target: string) => {
    const current = orderedColumns.map((c) => c.field);
    const next = moveColumn(current, source, target, fixedFields);
    if (next !== current) {
      pendingOrder.current = true;
      setOrder(next);
    }
  };
  const changeSort = (field: string, multi: boolean) => {
    const cycle = props.sortingOrder ?? ['asc', 'desc', null];
    const current = sortModel.find((s) => s.field === field)?.sort ?? null;
    const next = cycle[(cycle.indexOf(current) + 1) % cycle.length];
    const remaining = multi ? sortModel.filter((s) => s.field !== field) : [];
    setSort(next ? [...remaining, { field, sort: next }] : remaining);
  };
  const scrollToRow = (id: GridRowId) => {
    const index = visibleRows.findIndex((row) => getRowId(row.original) === id);
    if (index < 0) return false;
    virtualizer.scrollToIndex(index, { align: 'center' });
    return true;
  };
  const api: DataTableApi = {
    getAllColumns: () => orderedColumns,
    getVisibleColumns: () =>
      visibleColumns.map((c) => allColumns.find((def) => def.field === c.id)!),
    getFilteredSortedRowIds: () => visibleRows.map((r) => getRowId(r.original)),
    getRowsCount: () => rows.length,
    getSelectedRows: () =>
      new Map(rows.filter((row) => selected.has(getRowId(row))).map((row) => [getRowId(row), row])),
    getSortModel: () => sortRef.current,
    setSortModel: setSort,
    setFilterModel: setFilter,
    setRowSelectionModel: setSelection,
    subscribeEvent: (event, callback) => {
      if (!listeners.current.has(event)) listeners.current.set(event, new Set());
      listeners.current.get(event)!.add(callback);
      return () => {
        listeners.current.get(event)?.delete(callback);
      };
    },
    scrollToIndexes: ({ rowIndex }) => {
      if (rowIndex >= 0 && rowIndex < visibleRows.length)
        virtualizer.scrollToIndex(rowIndex, { align: 'center' });
    },
    scrollToRow,
    focusSearch: () => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
  };
  useLayoutEffect(() => {
    apiRef.current = api;
  });
  useEffect(
    () => () => {
      apiRef.current = null;
    },
    [apiRef],
  );
  const filterKey = JSON.stringify(filterModel);
  const selectionKey = JSON.stringify([...selected]);
  useEffect(() => {
    // Notify after the row model commits, so header totals/hidden-selection counts read current rows.
    listeners.current.forEach((callbacks) => callbacks.forEach((callback) => callback()));
  }, [visibleRows, filterKey, sortKey, selectionKey]);
  useLayoutEffect(() => {
    if (pendingOrder.current) {
      pendingOrder.current = false;
      props.onColumnOrderChange?.();
    }
  }, [orderedColumns, props.onColumnOrderChange]);

  const rowSpans = (row: R) =>
    visibleColumns.map((visibleColumn) => {
      const definition = allColumns.find((column) => column.field === visibleColumn.id)!;
      return (
        definition.colSpan?.(getCellValue(row, definition), row, definition, { current: api }) ?? 1
      );
    });

  const handleCellKey = (
    event: KeyboardEvent<HTMLDivElement>,
    rowIndex: number,
    colIndex: number,
  ) => {
    const row = visibleRows[rowIndex].original;
    const column = orderedColumns.find((c) => c.field === visibleColumns[colIndex].id)!;
    props.onCellKeyDown?.(
      { id: getRowId(row), row, field: column.field, value: getCellValue(row, column), api },
      event,
    );
    if (event.defaultPrevented || event.target !== event.currentTarget) return;
    if (
      event.key === ' ' &&
      props.checkboxSelection &&
      selectableVisibleIds.includes(getRowId(row))
    ) {
      event.preventDefault();
      toggleRow(getRowId(row), event.shiftKey);
      return;
    }
    let nextRow = rowIndex;
    let nextCol = colIndex;
    if (event.key === 'ArrowDown') nextRow++;
    else if (event.key === 'ArrowUp') nextRow--;
    else if (event.key === 'ArrowRight') nextCol += rowSpans(row)[colIndex];
    else if (event.key === 'ArrowLeft') nextCol--;
    else if (event.key === 'Home') {
      nextCol = 0;
      if (event.ctrlKey || event.metaKey) nextRow = 0;
    } else if (event.key === 'End') {
      nextCol = visibleColumns.length - 1;
      if (event.ctrlKey || event.metaKey) nextRow = visibleRows.length - 1;
    } else return;
    event.preventDefault();
    nextRow = Math.max(0, Math.min(visibleRows.length - 1, nextRow));
    nextCol = Math.max(0, Math.min(visibleColumns.length - 1, nextCol));
    nextCol = renderedCellIndex(nextCol, rowSpans(visibleRows[nextRow].original));
    const nextId = getRowId(visibleRows[nextRow].original);
    const field = visibleColumns[nextCol].id;
    setFocus({ row: nextId, field });
    virtualizer.scrollToIndex(nextRow, { align: 'auto' });
    requestAnimationFrame(() =>
      scrollRef.current
        ?.querySelector<HTMLElement>(
          `[data-id="${CSS.escape(String(nextId))}"] [data-field="${CSS.escape(field)}"]`,
        )
        ?.focus(),
    );
  };
  const selectedColumn = menu ? allColumns.find((c) => c.field === menu.field) : undefined;
  const openPanelAt = (anchor: HTMLElement, type: 'columns' | 'filters') => {
    const rect = anchor.getBoundingClientRect();
    setPanel({ type, position: { top: rect.bottom, left: rect.left } });
  };
  const openPanel = (type: 'columns' | 'filters') => {
    if (!menu) return;
    openPanelAt(menu.anchor, type);
    setMenu(null);
  };
  const activeFilterItems = filterModel.items.filter((item) =>
    isFilterItemActive(allColumns, item),
  );
  const toggleable = props.getTogglableColumns?.(allColumns);
  const canHide = (c: GridColDef) =>
    c.hideable !== false && (!toggleable || toggleable.includes(c.field));
  const Overlay = rows.length ? props.slots?.noResultsOverlay : props.slots?.noRowsOverlay;
  const LoadingOverlay = props.slots?.loadingOverlay;
  const ToolbarStart = props.slots?.toolbarStart;
  const ToolbarEnd = props.slots?.toolbarEnd;
  const summaryRows = visibleRows
    .map((row) => row.original)
    .filter((row) => !props.isSectionRow?.(row));
  const showLoadingOverlay = !!(props.loading && LoadingOverlay && !visibleRows.length);

  return (
    <div className={classNames(css.root, props.className, 'AppDataTable-root')}>
      {props.searchField && (
        <div className={css.toolbar}>
          {ToolbarStart && <ToolbarStart />}
          <TableSearchField
            inputRef={searchInputRef}
            value={search}
            onChange={changeSearch}
            placeholder={props.searchPlaceholder}
          />
          <span className={css.resultCount} role="status">
            {props.renderSearchSummary
              ? props.renderSearchSummary(summaryRows)
              : `${summaryRows.length.toLocaleString()} media`}
          </span>
          {ToolbarEnd && <ToolbarEnd />}
        </div>
      )}
      {props.loading && !showLoadingOverlay && (
        <LinearProgress className={css.loading} aria-label="Loading media" />
      )}
      <div
        className={css.grid}
        role="grid"
        aria-label={props['aria-label'] ?? 'Media table'}
        aria-rowcount={visibleRows.length + 1}
        aria-colcount={visibleColumns.length}
        aria-multiselectable={props.checkboxSelection || undefined}
        aria-busy={props.loading || undefined}
      >
        <div
          ref={headerRef}
          className={css.headerViewport}
          onScroll={() => syncScroll(headerRef.current, scrollRef.current)}
        >
          <div
            role="row"
            aria-rowindex={1}
            className={classNames(css.headers, 'AppDataTable-columnHeaders')}
            // Also over the scrollbar's gutter, which lets the strip scroll as far as the rows do.
            style={{
              height: columnHeaderHeight,
              width: table.getTotalSize() + scrollbarWidth,
              minWidth: '100%',
            }}
          >
            {table.getHeaderGroups()[0]?.headers.map((header, index) => {
              const column = allColumns.find((c) => c.field === header.id)!;
              const sort = sortModel.find((s) => s.field === column.field)?.sort;
              const cycle = props.sortingOrder ?? ['asc', 'desc', null];
              // What a click would sort by, hinted on hover the way the old grid did.
              const nextSort = sort ? null : cycle[(cycle.indexOf(null) + 1) % cycle.length];
              const sortable = column.sortable !== false;
              // Only filters that filter something. One still waiting for its value doesn't count,
              // or opening the filters alone would mark the column.
              const activeFilters = activeFilterItems.filter(
                (item) => item.field === column.field,
              ).length;
              const pinned = column.pinned === 'right';
              return (
                <div
                  key={header.id}
                  role="columnheader"
                  aria-colindex={index + 1}
                  aria-sort={
                    sort === 'asc' ? 'ascending' : sort === 'desc' ? 'descending' : undefined
                  }
                  data-field={column.field}
                  style={{
                    width: header.getSize(),
                    // Pinned past the scrollbar's gutter, which the header strip spans and the rows
                    // don't. The strip's rounded end is then over the gutter, not on this cell.
                    ...(pinned && {
                      right: scrollbarWidth,
                      borderRadius: scrollbarWidth ? 0 : undefined,
                    }),
                  }}
                  className={classNames(
                    css.header,
                    !column.disableColumnMenu && css.hasMenu,
                    pinned && css.pinnedRight,
                    'AppDataTable-columnHeader',
                  )}
                  draggable={!column.disableReorder}
                  onMouseDown={() => {
                    pressedResizeHandle.current = false;
                  }}
                  onDragStart={(e) => {
                    if (pressedResizeHandle.current) {
                      e.preventDefault();
                      return;
                    }
                    dragColumn.current = column.field;
                    e.dataTransfer.setData('text/plain', column.field);
                  }}
                  onDragEnd={() => {
                    dragColumn.current = null;
                  }}
                  onDragOver={(e) => {
                    if (dragColumn.current && !column.disableReorder) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragColumn.current) reorder(dragColumn.current, column.field);
                    dragColumn.current = null;
                  }}
                >
                  {column.field === '__check__' ? (
                    <TableCheckbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                      disabled={!selectableVisibleIds.length}
                      slotProps={{ input: { 'aria-label': 'Select all visible rows' } }}
                      onChange={() =>
                        setSelection({
                          type: 'include',
                          ids: toggleVisibleSelection(selected, selectableVisibleIds),
                        })
                      }
                    />
                  ) : (
                    <>
                      {/* The sort button is the keyboard's way in; a click anywhere else on the
                          title sorts too, so the target stays the column's width. */}
                      <div
                        className={classNames(
                          css.title,
                          sortable && css.sortable,
                          column.headerAlign === 'center' && css.centered,
                        )}
                        style={{
                          justifyContent:
                            column.headerAlign === 'right'
                              ? 'flex-end'
                              : column.headerAlign === 'center'
                                ? 'center'
                                : 'flex-start',
                        }}
                        onClick={(e) => sortable && changeSort(column.field, e.shiftKey)}
                      >
                        <button
                          type="button"
                          className={css.sortButton}
                          disabled={!sortable}
                          title={
                            sortable ? 'Sort; hold Shift to sort by multiple columns' : undefined
                          }
                        >
                          <span>{column.headerName ?? column.field}</span>
                          {sort === 'asc' && <ArrowUpwardRoundedIcon fontSize="inherit" />}
                          {sort === 'desc' && <ArrowDownwardRoundedIcon fontSize="inherit" />}
                        </button>
                        {activeFilters > 0 && (
                          <button
                            type="button"
                            className={css.filterButton}
                            aria-label={`${activeFilters} active ${activeFilters === 1 ? 'filter' : 'filters'}; show filters`}
                            title="Show filters"
                            onClick={(e) => {
                              e.stopPropagation(); // Not a sort.
                              openPanelAt(
                                e.currentTarget.closest<HTMLElement>('[role="columnheader"]')!,
                                'filters',
                              );
                            }}
                          >
                            <FilterAltOutlinedIcon fontSize="inherit" />
                          </button>
                        )}
                        {/* After the filter button, so appearing on hover doesn't move it. */}
                        {sortable && nextSort === 'asc' && (
                          <ArrowUpwardRoundedIcon fontSize="inherit" className={css.sortHint} />
                        )}
                        {sortable && nextSort === 'desc' && (
                          <ArrowDownwardRoundedIcon fontSize="inherit" className={css.sortHint} />
                        )}
                      </div>
                      {!column.disableColumnMenu && (
                        <IconButton
                          size="small"
                          className={css.menuButton}
                          aria-label={`${column.headerName || column.field} column options`}
                          aria-haspopup="menu"
                          onClick={(e) => setMenu({ anchor: e.currentTarget, field: column.field })}
                        >
                          <MoreVertRoundedIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </>
                  )}
                  {index < visibleColumns.length - 1 && (
                    <span className={css.headerDivider} aria-hidden="true" />
                  )}
                  {header.column.getCanResize() && (
                    <div
                      className={css.resizeHandle}
                      role="separator"
                      aria-label={`Resize ${column.headerName || column.field}`}
                      aria-orientation="vertical"
                      aria-valuenow={Math.round(header.getSize())}
                      aria-valuemin={column.minWidth ?? 50}
                      aria-valuemax={column.maxWidth ?? 2000}
                      tabIndex={0}
                      onMouseDown={(e) => {
                        // No text selection across the rows while dragging, and no reorder drag
                        // of the header either. The header still refuses that drag below, in case
                        // a browser starts one anyway.
                        e.preventDefault();
                        e.stopPropagation();
                        pressedResizeHandle.current = true;
                        header.getResizeHandler()(e);
                      }}
                      onTouchStart={header.getResizeHandler()}
                      onDoubleClick={() => header.column.resetSize()}
                      onKeyDown={(e) => {
                        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                        e.preventDefault();
                        const size = Math.max(
                          column.minWidth ?? 50,
                          Math.min(
                            column.maxWidth ?? 2000,
                            header.getSize() + (e.key === 'ArrowRight' ? 10 : -10),
                          ),
                        );
                        table.setColumnSizing((old) => ({ ...old, [header.id]: size }));
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div
          ref={scrollRef}
          className={classNames(css.scroller, 'AppDataTable-virtualScroller')}
          onScroll={() => syncScroll(scrollRef.current, headerRef.current)}
        >
          <div
            className={classNames(css.body, 'AppDataTable-virtualScrollerContent')}
            style={{
              height: virtualizer.getTotalSize(),
              width: table.getTotalSize(),
              minWidth: '100%',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = visibleRows[virtualRow.index].original;
              const id = getRowId(row);
              const selectable =
                props.checkboxSelection && props.isRowSelectable?.({ id, row }) !== false;
              let skip = 0;
              return (
                <div
                  key={String(id)}
                  {...props.slotProps?.row}
                  role="row"
                  aria-rowindex={virtualRow.index + 2}
                  aria-selected={props.checkboxSelection ? selected.has(id) : undefined}
                  data-id={id}
                  className={classNames(
                    css.row,
                    'AppDataTable-row',
                    selected.has(id) && css.selected,
                    props.getRowClassName?.({ id, row }),
                  )}
                  style={{
                    top: virtualRow.start + spacing(virtualRow.index).top,
                    height: height(virtualRow.index),
                    width: table.getTotalSize(),
                    minWidth: '100%',
                  }}
                  onClick={(event) => {
                    if (
                      (event.target as HTMLElement).closest(
                        'button,a,input,select,textarea,[role="button"]',
                      )
                    )
                      return;
                    props.onRowClick?.({ id, row }, event);
                    if (selectable && !props.disableRowSelectionOnClick)
                      toggleRow(id, event.shiftKey);
                  }}
                >
                  {visibleColumns.map((visibleColumn, colIndex) => {
                    if (skip) {
                      skip--;
                      return null;
                    }
                    const column = allColumns.find((c) => c.field === visibleColumn.id)!;
                    const value = getCellValue(row, column);
                    const span = Math.max(
                      1,
                      Math.min(
                        visibleColumns.length - colIndex,
                        column.colSpan?.(value, row, column, { current: api }) ?? 1,
                      ),
                    );
                    skip = span - 1;
                    const width = visibleColumns
                      .slice(colIndex, colIndex + span)
                      .reduce((sum, c) => sum + c.getSize(), 0);
                    const selectionControl = (
                      <TableCheckbox
                        size="small"
                        checked={selected.has(id)}
                        disabled={!selectable}
                        slotProps={{ input: { 'aria-label': 'Select row' } }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(_e) => toggleRow(id, (_e.nativeEvent as MouseEvent).shiftKey)}
                      />
                    );
                    const active =
                      focus && focusIsVisible
                        ? focus.row === id && focus.field === column.field
                        : virtualRow.index === 0 && colIndex === 0;
                    return (
                      <div
                        key={column.field}
                        role="gridcell"
                        aria-colindex={colIndex + 1}
                        aria-colspan={span > 1 ? span : undefined}
                        data-field={column.field}
                        tabIndex={active ? 0 : -1}
                        className={classNames(
                          css.cell,
                          column.pinned === 'right' && css.pinnedRight,
                          'AppDataTable-cell',
                        )}
                        style={{
                          width,
                          justifyContent:
                            span > 1
                              ? 'flex-start'
                              : column.align === 'right'
                                ? 'flex-end'
                                : column.align === 'center'
                                  ? 'center'
                                  : 'flex-start',
                        }}
                        onFocus={() => setFocus({ row: id, field: column.field })}
                        onKeyDown={(event) => handleCellKey(event, virtualRow.index, colIndex)}
                      >
                        {column.renderCell
                          ? column.renderCell({
                              id,
                              row,
                              field: column.field,
                              value,
                              api,
                              selectionControl,
                            })
                          : column.valueFormatter
                            ? column.valueFormatter(value)
                            : String(value ?? '')}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {showLoadingOverlay && LoadingOverlay && (
            <div className={css.loadingOverlay}>
              <LoadingOverlay />
            </div>
          )}
          {!visibleRows.length && !props.loading && (
            <div className={css.empty} role="status">
              {search.trim() ? (
                <div className={css.emptySearch}>
                  <span>{props.searchEmptyText ?? 'No media match your search and filters.'}</span>
                  <Button
                    onClick={() => {
                      changeSearch('');
                      setFilter(EMPTY_FILTER);
                    }}
                  >
                    {activeFilterItems.length ? 'Clear search and filters' : 'Clear search'}
                  </Button>
                </div>
              ) : Overlay ? (
                <Overlay />
              ) : rows.length ? (
                'No results match your filters.'
              ) : (
                'No media to display.'
              )}
            </div>
          )}
        </div>
      </div>
      <Menu
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        slotProps={{ paper: { className: css.columnMenu } }}
      >
        {selectedColumn?.sortable !== false && (
          <MenuItem
            onClick={() => {
              setSort([{ field: menu!.field, sort: 'asc' }]);
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <ArrowUpwardRoundedIcon fontSize="small" />
            </ListItemIcon>
            Sort ascending
          </MenuItem>
        )}
        {selectedColumn?.sortable !== false && (
          <MenuItem
            onClick={() => {
              setSort([{ field: menu!.field, sort: 'desc' }]);
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <ArrowDownwardRoundedIcon fontSize="small" />
            </ListItemIcon>
            Sort descending
          </MenuItem>
        )}
        {!props.disableColumnFilter && selectedColumn?.filterable !== false && (
          <MenuItem
            onClick={() => {
              const column = selectedColumn!;
              if (!filterModel.items.some((item) => item.field === column.field))
                setFilter({
                  ...filterModel,
                  items: [
                    ...filterModel.items,
                    {
                      id: crypto.randomUUID(),
                      field: column.field,
                      operator: getOperators(column)[0].value,
                    },
                  ],
                });
              openPanel('filters');
            }}
          >
            <ListItemIcon>
              <FilterAltOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Filter
          </MenuItem>
        )}
        {(selectedColumn?.sortable !== false ||
          (!props.disableColumnFilter && selectedColumn?.filterable !== false)) && <Divider />}
        {selectedColumn && canHide(selectedColumn) && (
          <MenuItem
            disabled={visibleColumns.length <= 1}
            onClick={() => {
              setVisibility({ ...visibility, [menu!.field]: false });
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <VisibilityOffOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Hide column
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            openPanel('columns');
          }}
        >
          <ListItemIcon>
            <ViewColumnOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Manage columns
        </MenuItem>
        {selectedColumn && !selectedColumn.disableReorder && <Divider />}
        {selectedColumn &&
          !selectedColumn.disableReorder &&
          ([-1, 1] as const).map((direction) => {
            const index = orderedColumns.findIndex((c) => c.field === selectedColumn.field);
            const target = orderedColumns[index + direction];
            return (
              <MenuItem
                key={direction}
                disabled={!target || !!target.disableReorder}
                onClick={() => {
                  reorder(selectedColumn.field, target.field);
                  setMenu(null);
                }}
              >
                <ListItemIcon>
                  {direction < 0 ? (
                    <ArrowBackRoundedIcon fontSize="small" />
                  ) : (
                    <ArrowForwardRoundedIcon fontSize="small" />
                  )}
                </ListItemIcon>
                Move {direction < 0 ? 'left' : 'right'}
              </MenuItem>
            );
          })}
      </Menu>
      <Popover
        open={!!panel}
        anchorReference="anchorPosition"
        anchorPosition={panel?.position}
        onClose={() => setPanel(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            className: css.panel,
            role: 'dialog',
            'aria-label': panel?.type === 'filters' ? 'Table filters' : 'Manage columns',
          },
        }}
      >
        {panel?.type === 'filters' ? (
          <FilterPanel
            columns={allColumns}
            model={filterModel}
            onChange={setFilter}
            onClose={() => setPanel(null)}
          />
        ) : (
          <div className={css.columnsPanel}>
            <div className={css.panelHeading}>
              <strong>Columns</strong>
              <IconButton aria-label="Close columns" onClick={() => setPanel(null)}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </div>
            <input
              className={css.input}
              aria-label="Search columns"
              placeholder="Search columns"
              value={columnSearch}
              onChange={(e) => setColumnSearch(e.target.value)}
            />
            {orderedColumns
              .filter(
                (c) =>
                  canHide(c) &&
                  (c.headerName || c.field).toLowerCase().includes(columnSearch.toLowerCase()),
              )
              .map((column) => (
                <FormControlLabel
                  key={column.field}
                  label={column.headerName || column.field}
                  control={
                    <TableCheckbox
                      size="small"
                      checked={visibility[column.field] !== false}
                      disabled={visibleColumns.length <= 1 && visibility[column.field] !== false}
                      onChange={(_, checked) =>
                        setVisibility({ ...visibility, [column.field]: checked })
                      }
                    />
                  }
                />
              ))}
          </div>
        )}
      </Popover>
    </div>
  );
}
